import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import ColorOptionsClipboardButtons from '@/components/DesignStudio/ColorOptionsClipboardButtons.vue';
import CButton from '@/components/shared/CButton';
import CInput from '@/components/shared/CInput';
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { useColor } from '@/composables/color';
import { colorsMapStore, useColorOptions } from '@/composables/colorOptions';
import { useColorOptionsClipboard } from '@/composables/useColorOptionsClipboard';
import { useToaster } from '@/composables/toaster';
import { DatadogEvent, fireDatadogLog } from '@/utils/datadogUtils';
import { recordFeatureUsage, TrackedFeature } from '@/utils/featureUsage';


export default /*#__PURE__*/_defineComponent({
  __name: 'ImageOutlineProperties',
  props: {
    canvas: null,
    imageElement: null,
    editMode: { type: Boolean },
    cmykMode: { type: Boolean }
  },
  emits: ["open-outline-color-library-selector"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'open-outline-color-library-selector'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    canvas: CanvasEx;
    imageElement?: ImagePlaceholderElement;
    editMode?: boolean;
    cmykMode?: boolean;
};

/**
 * Outline panel of an image placeholder: its width and the color options the buyer picks from.
 *  Same controls as the stroke of a text (see TextColorProperties).
 */
const COLOR_TYPE = LibraryTypeEnum.Color;
const INITIAL_OUTLINE_COLOR = '#FF0054';
// The filter scales the width with the image diagonal: 10 is already a very thick outline
const INITIAL_OUTLINE_WIDTH = 2;
const MAX_OUTLINE_WIDTH = 10;





const outlineWidth = ref(0);

// Not derived from the width: typing a 0 in the input must not collapse the section under the cursor
const isOutlineEnabled = ref(false);

watch(
    () => props.imageElement,
    (element) => {
        outlineWidth.value = element?.outlineWidth || 0;
        isOutlineEnabled.value = outlineWidth.value > 0;
    },
    { immediate: true }
);

// A single color image is traced by potrace to one flat color, which leaves nothing to outline
const disabled = computed(() => {
    const element = props.imageElement;
    return !element || element.locked || !(element.fullColor || element.grading);
});

const colors = useColorOptions<ImagePlaceholderElement>({
    store: colorsMapStore(
        { colorsMap: 'outlineColorsMap', option: 'outlineColorOption', libraryId: 'outlineColorLibraryId' },
        (element, css) => element.setOutlineColor(css)
    ),
    element: () => props.imageElement,
    canvas: () => props.canvas,
    editMode: () => props.editMode,
    openLibrarySelector: () => emit('open-outline-color-library-selector')
});

const { getColorFromString, cmykToDecimalString } = useColor();
const toColorObject = (color: string): ColorObject => {
    const { hex, a, cmyk } = getColorFromString(color);
    return { hex, alpha: a, cmyk: cmykToDecimalString(cmyk) };
};

const updateOutlineWidth = (value: number | string) => {
    const element = props.imageElement;
    if (!element) return;

    outlineWidth.value = Number(value) || 0;
    element.setOutlineWidth(outlineWidth.value);
    if (props.editMode) {
        element.setEditedProp('outlineWidth');
    }
    props.canvas.requestRenderAll();
};

const addOutline = () => {
    isOutlineEnabled.value = true;
    // Colors first, so the outline filter runs once and already with its color
    colors.replaceColors(new Map([[1, toColorObject(INITIAL_OUTLINE_COLOR)]]));
    updateOutlineWidth(INITIAL_OUTLINE_WIDTH);
};

const removeOutline = () => {
    isOutlineEnabled.value = false;
    updateOutlineWidth(0);
    // An empty map, never null: the backend would bring back the legacy outlineColor of the element
    colors.replaceColors(new Map());
};

/* Copy/paste color options, shared with the Fill/Stroke/Shadow/Vector sections */
const clipboard = useColorOptionsClipboard();
const toast = useToaster();

// The outline is a solid color, so a Fill clip carrying a gradient or palette can't land here
const canPasteColors = computed(() => {
    const clip = clipboard.clip();
    return !disabled.value && !!clip && !clip.hasNonSolidColor;
});

const onCopyColors = () => {
    clipboard.copy({
        colors: new Map(colors.model.colorsMap),
        libraryId: colors.model.libraryId
    });
    fireDatadogLog(DatadogEvent.CopyDsColorOptions);
    toast.show('Outline colors copied');
};

const onPasteColors = () => {
    const clip = clipboard.clip();
    if (!canPasteColors.value || !clip) return;

    colors.replaceColors(clip.colors, clip.libraryId);
    // Pasting into an image with no outline yet would otherwise be invisible
    if (!isOutlineEnabled.value) {
        isOutlineEnabled.value = true;
        updateOutlineWidth(INITIAL_OUTLINE_WIDTH);
    }
    fireDatadogLog(DatadogEvent.PasteDsColorOptions);
    recordFeatureUsage(TrackedFeature.ColorsPasteImageOutline);
    toast.show('Outline colors pasted');
};

return { __sfc: true,COLOR_TYPE, INITIAL_OUTLINE_COLOR, INITIAL_OUTLINE_WIDTH, MAX_OUTLINE_WIDTH, props, emit, outlineWidth, isOutlineEnabled, disabled, colors, getColorFromString, cmykToDecimalString, toColorObject, updateOutlineWidth, addOutline, removeOutline, clipboard, toast, canPasteColors, onCopyColors, onPasteColors, ElementLibrarySelector, ColorOptionsClipboardButtons, CButton, CInput }
}

})